import { Stack, Typography, useTheme } from "@mui/material";
import { TrendingUp } from "lucide-react";
import { type FC, useState } from "react";
import type {
	ClientPortfolioProducerInstrumentResponseDTO,
	ClientPortfolioToolReportingSummaryResponseDTO,
} from "web_myplann/Infrastructure/Api/Api";
import { DetailActionRow } from "web_myplann/Portfolio/Components/Redesign/Detail/DetailActionRow";
import {
	FundDetailDrawer,
	type FundDetailSelection,
} from "web_myplann/Portfolio/Components/Redesign/Detail/FundDetail/FundDetailDrawer";
import { useFundRows } from "web_myplann/Portfolio/Components/Redesign/Detail/Sections/useFundRows";
import type { NormalizedTool } from "web_myplann/Portfolio/Helpers/investmentToolTypes";
import { IconBox } from "web_myplann/Shared/Components/IconBox";

type Props = {
	portfolioId: number;
	title: string;
	tools: NormalizedTool[];
	producerInstruments?: ClientPortfolioProducerInstrumentResponseDTO[] | null;
	toolsSummary?: ClientPortfolioToolReportingSummaryResponseDTO[];
	currencySign: string;
};

export const FundListSection: FC<Props> = ({
	portfolioId,
	title,
	tools,
	producerInstruments,
	toolsSummary,
	currencySign,
}) => {
	const theme = useTheme();
	const [selection, setSelection] = useState<FundDetailSelection>();

	const rows = useFundRows({
		tools,
		producerInstruments,
		toolsSummary,
		currencySign,
	});

	if (rows.length === 0) {
		return null;
	}

	return (
		<Stack>
			<Typography variant="sectionLabel">{title}</Typography>
			<Stack bgcolor={theme.palette.background.paper}>
				{rows.map((row) => (
					<DetailActionRow
						key={row.key}
						truncate
						icon={
							<IconBox bg={theme.palette.purple[50]} size={36}>
								<TrendingUp size={18} color={theme.palette.purple[600]} />
							</IconBox>
						}
						label={row.label}
						element={
							row.value ? (
								<Typography variant="bodyStrong" tabular>
									{row.value}
								</Typography>
							) : undefined
						}
						onClick={() => setSelection(row.selection)}
					/>
				))}
			</Stack>

			<FundDetailDrawer
				open={selection !== undefined}
				onClose={() => setSelection(undefined)}
				portfolioId={portfolioId}
				selection={selection}
				currencySign={currencySign}
				title={title}
			/>
		</Stack>
	);
};
